/**
 * Contas da Visão Geral que o modelo do dono define (nucleo-visao-geral-v3.html).
 * Funções puras, sem React.
 */
import { diaSemanaSeg } from "./formato";

/** Celular: o gráfico rola de lado; abre centrado no ponto de hoje (fração 0–1). */
export function centrarRolagem(el, fracao) {
  if (!el || el.scrollWidth <= el.clientWidth) return;
  el.scrollLeft = Math.max(0, fracao * el.scrollWidth - el.clientWidth / 2);
}

/** Passo "redondo" do eixo para ~5 linhas até `max`. */
export function passoEixo(max) {
  const alvo = Math.max(1, max) / 5;
  const base = Math.pow(10, Math.floor(Math.log10(alvo)));
  for (const m of [1, 2, 2.5, 5, 10]) if (base * m >= alvo) return base * m;
  return base * 10;
}

/** Curva suave (Catmull-Rom → Bézier) por uma lista de pontos [x, y]. */
export function curva(pts) {
  if (!pts.length) return "";
  if (pts.length < 2) return `M${pts[0][0]} ${pts[0][1]}`;
  let d = `M${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    d += ` C${p1[0] + (p2[0] - p0[0]) / 6} ${p1[1] + (p2[1] - p0[1]) / 6} ${p2[0] - (p3[0] - p1[0]) / 6} ${p2[1] - (p3[1] - p1[1]) / 6} ${p2[0]} ${p2[1]}`;
  }
  return d;
}

/** Trechos contínuos (sem null) de uma série, entre os índices `de` e `ate`. */
export function trechos(serie, de, ate, x, y) {
  const out = [];
  let cur = [];
  for (let i = de; i <= ate; i++) {
    const v = serie[i];
    if (v == null) { if (cur.length) out.push(cur); cur = []; continue; }
    cur.push([x(i), y(v)]);
  }
  if (cur.length) out.push(cur);
  return out;
}

/**
 * Faturamento mensal (regras do modelo):
 *  - média = meses FECHADOS desde o começo (mês com 0 entra como zero);
 *  - mês atual: vendido + dias que faltam × média diária do mês × 0,9;
 *  - meses seguintes: mesmo mês do ano passado × crescimento médio dos meses
 *    fechados que têm par no ano passado, limitado ao melhor mês já feito;
 *    sem par: repete a média.
 */
export function projecaoMensal({ atual, anterior, mesAtual, diaHoje, diasNoMes }) {
  const inicio = atual.findIndex((v) => v != null);
  const fechados = [];
  for (let i = Math.max(0, inicio); i < mesAtual; i++) if (inicio >= 0 && atual[i] != null) fechados.push(i);
  const media = fechados.length ? fechados.reduce((a, i) => a + atual[i], 0) / fechados.length : null;
  const pares = fechados.filter((i) => anterior[i] > 0 && atual[i] > 0);
  const cresc = pares.length ? pares.reduce((a, i) => a + atual[i] / anterior[i], 0) / pares.length : 1;
  const teto = fechados.length ? Math.max(...fechados.map((i) => atual[i])) : null;
  const vendido = atual[mesAtual] || 0;
  const proj = {};
  proj[mesAtual] = Math.round(vendido + (diasNoMes - diaHoje) * (diaHoje ? vendido / diaHoje : 0) * 0.9);
  for (let i = mesAtual + 1; i < 12; i++) {
    let v = anterior[i] > 0 && pares.length ? anterior[i] * cresc : media ?? 0;
    if (teto != null) v = Math.min(teto, v);
    proj[i] = Math.round(v);
  }
  return { inicio, media, proj };
}

/**
 * Faturamento por dia do mês (regras do modelo):
 *  - média = dias fechados (até ontem);
 *  - hoje = vendido + o que ainda é esperado nas horas que faltam;
 *  - dias seguintes = média do mesmo dia da semana no mês × 0,9.
 * `diaSemana(d)` devolve o dia da semana do dia d (qualquer convenção, só agrupa).
 */
export function projecaoDiaria({ valores, diasNoMes, esperadoRestanteHoje, diaSemana }) {
  const diaHoje = valores.length;
  const fechados = valores.slice(0, diaHoje - 1);
  const media = fechados.length ? fechados.reduce((a, b) => a + b, 0) / fechados.length : null;
  const porDia = {};
  fechados.forEach((v, i) => { (porDia[diaSemana(i + 1)] = porDia[diaSemana(i + 1)] || []).push(v); });
  const mediaDoDia = (w) => (porDia[w] ? porDia[w].reduce((a, b) => a + b, 0) / porDia[w].length : media ?? 0);
  const proj = {};
  proj[diaHoje] = Math.round((valores[diaHoje - 1] || 0) + Math.max(0, esperadoRestanteHoje || 0));
  for (let d = diaHoje + 1; d <= diasNoMes; d++) proj[d] = Math.round(mediaDoDia(diaSemana(d)) * 0.9);
  return { media, proj };
}

/** Quanto ainda se espera vender hoje (R$ ou pedidos) nas horas que faltam. */
export function restanteDeHoje(mes, campo) {
  const ultimo = mes.dias[mes.dias.length - 1];
  const esp = mes.esperado_por_hora[diaSemanaSeg(mes.hoje)];
  let resto = 0;
  for (let h = mes.hora_agora; h < 24; h++) {
    const e = esp[h][campo];
    resto += h === mes.hora_agora ? Math.max(0, e - (ultimo?.horas[h]?.[campo] || 0)) : e;
  }
  return resto;
}
